import { useCallback, useContext, useEffect, useState } from "react";
import { DatabaseContext } from "../DatabaseContext";
import { ParentContext } from "../ParentContext";
import { _showAppLogs, LogContext } from "../LogContext";
import { showAppManagementTypes } from "global/cnr/reducers/AppUserManagementReducer";
import { _profileCollectionName } from "global/firestoreData/profiles/getProfile";
import { dataFix } from "global/common/dataFix";
import { createRefPath_client } from "global/firestoreData/appData/appRefPaths";
import { writeBatch } from "firebase/firestore";
import { logBatch } from "global/cnr/reducers/reducerHelpers/logging";

const _allowUpdate = true

export const useClientProfiles = ({ appManagementType }) => {

  // logContext
  const logContext = useContext(LogContext)
  const { log_handlers } = logContext ?? {}

  const parentContext = useContext(ParentContext);
  const { states } = parentContext
  const { app_state, currentSettings_state, paps_state } = states
  const { pathViews } = paps_state ?? {}
  const { mainDb } = app_state ?? {}

  const { aps_appUserSettings } = _.get(currentSettings_state, 'gvviSettings', {});
  const { appUserCollection } = aps_appUserSettings ?? {}

  const databaseContext = useContext(DatabaseContext);
  const { database_fns } = databaseContext ?? {};

  const [clientProfiles, setClientProfiles] = useState()

  useEffect(() => {

    const getData = async () => {

      // 2️⃣ Listen for Firestore updates
      const cb = async (fetchedData) => {
        _showAppLogs.dbFetch && log_handlers.addLog(`🌐 Fetched data(${appUserCollection}) from Firebase`);
        setClientProfiles(fetchedData);
      };

      try {
        getAllClientProfiles(database_fns, pathViews, pathViews.events, appUserCollection, cb)
      } catch (err) {
        _showAppLogs.dbFetch && log_handlers.addLog(`❌ Failed to fetch data(${appUserCollection})`, err);
      }
    }

    switch (appManagementType) {
      case showAppManagementTypes.getClientProfiles:
        getData()
        break;
      default:
      // nothing
    }

  }, [appManagementType]);

  const updateClientProfiles = useCallback(
    async (clientProfiles, merge, remove) => {
      if (remove) {
        return removeClientProfiles(mainDb, database_fns, clientProfiles, pathViews)
      } else {
        return updateClient_profiles(mainDb, database_fns, clientProfiles, pathViews, merge);
      }
    },
    []
  );


  return { clientProfiles, updateClientProfiles };
};

const updateClient_profiles = async (mainDb, database_fns, clientProfiles, pathViews, merge) => {

  const useTemp = false
  const collectionName = useTemp ? '_' + _profileCollectionName : _profileCollectionName

  const batch = writeBatch(mainDb);
  _.forEach(clientProfiles, (cp, key) => {
    const { email } = cp ?? {}
    delete cp._itemKey
    delete cp.intials
    const _cp = dataFix.removeAllEmpties(cp)
    const _refPath = createRefPath_client(pathViews, [collectionName, email])
    database_fns.set_doc(_refPath, _cp, merge, null, batch)
  })

  logBatch(batch, clientProfiles)

  if (_allowUpdate) {
    batch.commit()
  }
}

const removeClientProfiles = async (mainDb, database_fns, clientProfiles, pathViews) => {

  const useTemp = false
  const collectionName = useTemp ? '_' + _profileCollectionName : _profileCollectionName

  const batch = writeBatch(mainDb);
  _.forEach(clientProfiles, (cp, key) => {
    delete cp._itemKey
    delete cp.intials
    const _refPath = createRefPath_client(pathViews, [collectionName, key])
    database_fns.delete_doc(_refPath, null, batch)
  })

  logBatch(batch, clientProfiles)

  if (_allowUpdate) {
    batch.commit()
  }
}

const getAllClientProfiles = async (database_fns, pathViews, eventKey, appUserCollection, callback, fsdb) => {

  const _refPath_profiles = createRefPath_client(pathViews, ['profiles'])
  const _refPath_events = eventKey ? createRefPath_client(pathViews, ['events', eventKey]) : createRefPath_client(pathViews, ['events'])

  // get all the PROFILES for the CLIENTS
  const clientProfiles = await database_fns.get_data({ refPath: _refPath_profiles, fsdb })

  // get all the EVENTS for the CLIENTS
  const clientEvents = await database_fns.get_data({ refPath: _refPath_events, fsdb })
  const uniqueProfiles = await getUniqueProfiles(database_fns, pathViews)

  const profileKeys = clientProfiles ? Object.keys(clientProfiles) : []
  const uniqueProfileKeys = uniqueProfiles ? Object.keys(uniqueProfiles) : []
  const eventKeys = clientEvents ? Object.keys(clientEvents) : []

  if (clientEvents) {

    // get all the attendees for each of the client's events
    getAllClientAppUsers(database_fns, pathViews, clientEvents, appUserCollection).then(res => {

      const attendees_event = {}
      const profiles_existing = {}
      const profiles_delete = {}
      const profiles_modified = {}
      const profiles_alt = {}

      _.forEach(eventKeys, (evk, index) => {
        if (evk.indexOf('_backUp') < 0) {
          const _event = clientEvents[evk]
          const { name: eventName } = _event ?? {}
          const attendees = res[index] ? res[index] : null
          attendees_event[evk] = attendees

          // loop the attendess in the event
          _.forEach(attendees_event, (attendees, ek) => {

            // loop the profileKeys to try and find an email match
            profileKeys.forEach(pk => {

              const uniqueProfile = uniqueProfiles ? uniqueProfiles[pk] : null

              let isAlt;

              const profile = clientProfiles[pk]

              let attendeeKey = _.findKey(attendees, { email: pk })

              // if no attendeeKey, check the attendees with a lower case email
              if (!attendeeKey) {
                const { email } = profile ?? {}
                attendeeKey = email ? _.findKey(attendees, { email: email.toLowerCase() }) : null
                isAlt = true
              }


              if (attendeeKey) {

                const pk_lc = pk.toLowerCase()
                const isDelete = pk_lc !== pk

                let _currentProfiles = isDelete ? profiles_delete : profiles_existing
                let _pk = pk

                if (isAlt) {
                  _currentProfiles = profiles_delete
                  // _pk = pk.toLowerCase()
                  profile.email = profile.email.toLowerCase()
                }

                if (!_currentProfiles[_pk]) {
                  _currentProfiles[_pk] = { ...profile }
                  _currentProfiles[_pk].events = {}
                }

                if (!_currentProfiles[_pk].events) { _currentProfiles[_pk].events = {} }

                if (!_currentProfiles[_pk].events[ek]) {
                  _currentProfiles[_pk].events[ek] = {
                    name: eventName,
                    appUserKey: attendeeKey,
                    // eventKey: ek
                  }
                }

                if (!_currentProfiles[_pk].events[ek].eventKey) {
                  _currentProfiles[_pk].events[ek].eventKey = ek
                }

                if (uniqueProfile && _currentProfiles[_pk].events) {
                  _currentProfiles[_pk].events = { ..._currentProfiles[_pk].events, ...uniqueProfile.events }
                }
              }
            })
          })
        }
      })

      ammendModified(profiles_delete, profiles_existing, profiles_modified)
      ammendModified(profiles_alt, profiles_existing, profiles_modified)

      callback({ profiles_existing, profiles_delete, profiles_modified, profiles_alt, profiles_combined: { ...profiles_existing, ...profiles_modified } })
    })
  }
}

const getUniqueProfiles = async (database_fns, pathViews) => {
  return new Promise(async (resolve, reject) => {
    getClientEventAttendees(database_fns, pathViews).then(allAttendees => {
      getUniqueAttedees(allAttendees).then(uniqueAttedees => {
        resolve(uniqueAttedees)
      })
    })
  })
}

const ammendModified = (profiles, profiles_existing, profiles_modified) => {
  Object.keys(profiles).forEach(key => {
    const apd = profiles[key]
    const { email } = apd
    const key_lc = key.toLowerCase()
    if (!profiles_existing[key_lc]) {
      profiles_modified[key_lc] = { ...apd }
      profiles_modified[key_lc].email = email.toLowerCase()
    }
  })
}

/**
 * 
 * @param {object} pathViews 
 * @param {object} events  
 * @returns a promise with all the attendees for each of the events
 */
const getAllClientAppUsers = async (database_fns, pathViews, events, appUserCollection) => {
  const promises = []
  _.forEach(events, (event, eventKey) => {
    if (eventKey.indexOf('_backUp') < 0) {
      const _refPath_attendees = createRefPath_client(pathViews, ['events', eventKey, appUserCollection])
      promises.push(database_fns.get_data({ refPath: _refPath_attendees }))
    }
  })
  return Promise.all(promises)
}

const getClientEventAttendees = async (database_fns, pathViews) => {

  return new Promise(async (resolve, reject) => {

    const _refPath_clients = createRefPath_client(pathViews, ['events'])

    try {
      const _clientEvents = await database_fns.get_data({ refPath: _refPath_clients })

      const allAttendees = [];

      await Promise.all(
        Object.keys(_clientEvents).map(async eventKey => {
          const _refPath_attendees = createRefPath_client(pathViews, ['events', eventKey, 'attendees'])
          const eventAttendees = await database_fns.get_data({ refPath: _refPath_attendees })
          Object.keys(eventAttendees).forEach(async (attedeeKey) => {
            const attendee = eventAttendees[attedeeKey]
            allAttendees.push({ ...attendee, eventKey, eventName: _clientEvents[eventKey].name });
          });
        })
      )

      resolve(allAttendees);

    } catch (error) {
      // Reject the promise if there's an error
      reject(error);
    }
  });
};

const getUniqueAttedees = async (allAttendees) => {

  return new Promise(async (resolve, reject) => {
    const uniqueAttedees = {}
    if (allAttendees && allAttendees.length > 0) {
      allAttendees.forEach(async attendee => {
        const { email, _itemKey, eventKey, eventName } = attendee
        if (!uniqueAttedees[email]) {
          uniqueAttedees[email] = {
            events: {
              [eventKey]: {
                appUserKey: _itemKey,
                name: eventName,
                eventKey,
              }
            }
          }
        } else {
          uniqueAttedees[email].events[eventKey] = {
            appUserKey: _itemKey,
            name: eventName,
            eventKey,
          }
        }
      })
    }
    resolve(uniqueAttedees);
  })


}